import React, { useEffect, useState } from 'react';
import { supervisedStudentApi } from '@/features/workspace/api';
import '../tp201-projects.css';

interface SharedProjectsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenLiterature?: () => void;
}

type ProjectTab = 'overview' | 'topic' | 'plan' | 'tasks' | 'literature' | 'templates' | 'meetings' | 'results';

interface ProjectTask {
  title: string;
  deadline: string;
  status: string;
}

interface ProjectMeeting {
  date: string;
  time: string;
  title: string;
  status: string;
}

interface ResearchProject {
  id: string;
  title: string;
  description: string;
  stage: string;
  period: string;
  progress: number;
  role: string;
  supervisor: string;
  topic: string;
  goal: string;
  deadline: string;
  tasks: ProjectTask[];
  materials: string[];
  literature: string[];
  savedLiterature: string[];
  meetings: ProjectMeeting[];
  results: Array<{ title: string; status: string }>;
  inPortfolio: boolean;
}

const TABS: Array<[ProjectTab, string]> = [
  ['overview', 'Обзор проекта'],
  ['topic', 'Тема и роль'],
  ['plan', 'План и этапы'],
  ['tasks', 'Задачи и материалы'],
  ['literature', 'Литература проекта'],
  ['templates', 'Образцы и документы'],
  ['meetings', 'Встречи и консультации'],
  ['results', 'Презентации и результаты'],
];

const TEMPLATES = [
  ['ПАСПОРТ ПРОЕКТА', 'Паспорт / краткое описание проекта', 'Цель, команда, этапы, сроки и ожидаемые результаты.'],
  ['ИНДИВИДУАЛЬНЫЙ ПЛАН', 'План участника проекта', 'Личная роль, задачи, дедлайны и подтверждение выполнения.'],
  ['ДНЕВНИК', 'Дневник исследователя', 'Хронология действий, решений, источников и консультаций.'],
  ['ПРЕЗЕНТАЦИЯ', 'Промежуточная презентация', 'Структура 7–10 слайдов: задача, материал, метод, текущий результат, следующий этап.'],
  ['ПОСТЕР', 'Научный постер A1', 'Название, авторы, задача, данные, метод, результаты, выводы и QR-ссылка.'],
  ['ФИНАЛ', 'Итоговая презентация проекта', 'Проблема → вклад → данные → метод → результат → ограничения → вывод.'],
  ['ОТЧЁТ', 'Финальный отчёт', 'Выполненные задачи, индивидуальный вклад, результат и приложения.'],
  ['ТЕЗИСЫ', 'Abstract / тезисы', 'Краткий формат для конференции или семинара по результатам проекта.'],
];

const COLORS = ['#49ddb7', '#41d2ff', '#e8b33b', '#9e80ff'];

const STATUS: Record<string, [string, string]> = {
  DONE: ['Выполнено', 'ok'],
  Выполнено: ['Выполнено', 'ok'],
  completed: ['Выполнено', 'ok'],
  IN_PROGRESS: ['В работе', 'warn'],
  'В работе': ['В работе', 'warn'],
  in_progress: ['В работе', 'warn'],
  PLANNED: ['Запланировано', 'info'],
  Запланировано: ['Запланировано', 'info'],
  BOOKED: ['Запланировано', 'info'],
  pending: ['Запланировано', 'info'],
};

function statusOf(value: string): [string, string] {
  return STATUS[value] || [value || '—', 'info'];
}

function normalizeProject(raw: Record<string, unknown>): ResearchProject {
  const tasks = Array.isArray(raw.tasks) ? raw.tasks.map((item) => {
    if (Array.isArray(item)) return { title: String(item[0] || ''), deadline: String(item[1] || ''), status: String(item[2] || '') };
    const row = item as Record<string, unknown>;
    return { title: String(row.title || ''), deadline: String(row.deadline || ''), status: String(row.status || '') };
  }) : [];
  const materials = Array.isArray(raw.materials)
    ? raw.materials.map((item) => typeof item === 'string' ? item : String((item as Record<string, unknown>).file_name || ''))
    : [];
  const results = Array.isArray(raw.results)
    ? raw.results.map((item) => {
      const row = item as Record<string, unknown>;
      return { title: String(row.title || item || ''), status: String(row.status || 'PLANNED') };
    })
    : [];
  const meetings = Array.isArray(raw.meetings)
    ? raw.meetings.map((item) => {
      if (Array.isArray(item)) return { date: String(item[0] || ''), time: String(item[1] || ''), title: String(item[2] || ''), status: String(item[3] || 'PLANNED') };
      const row = item as Record<string, unknown>;
      return { date: String(row.date || ''), time: String(row.time || ''), title: String(row.title || ''), status: String(row.status || 'PLANNED') };
    })
    : [];
  return {
    id: String(raw.id || ''),
    title: String(raw.title || ''),
    description: String(raw.description || raw.topic_description || raw.desc || ''),
    stage: String(raw.stage || ''),
    period: String(raw.period || ''),
    progress: Number(raw.progress ?? raw.progress_pct ?? raw.progress_percentage ?? 0),
    role: String(raw.role || raw.my_role || raw.role_in_project || ''),
    supervisor: String(raw.supervisor || raw.supervisor_name || ''),
    topic: String(raw.topic || raw.topic_description || ''),
    goal: String(raw.goal || ''),
    deadline: String(raw.deadline || raw.nextDeadline || ''),
    tasks,
    materials,
    literature: Array.isArray(raw.literature) ? raw.literature.map(String) : [],
    savedLiterature: Array.isArray(raw.saved_literature) ? raw.saved_literature.map(String) : [],
    meetings,
    results,
    inPortfolio: Boolean(raw.in_portfolio ?? raw.inPortfolio),
  };
}

export const SharedProjectsOverlay: React.FC<SharedProjectsOverlayProps> = ({
  isOpen,
  onClose,
  onOpenLiterature,
}) => {
  const [projects, setProjects] = useState<ResearchProject[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [tab, setTab] = useState<ProjectTab>('overview');
  const [notice, setNotice] = useState('');
  const [consultText, setConsultText] = useState('');
  const [sample, setSample] = useState<string[] | null>(null);

  const load = async () => {
    const rows = typeof supervisedStudentApi.researchProjects === 'function'
      ? await supervisedStudentApi.researchProjects()
      : [await supervisedStudentApi.project()].filter(Boolean);
    const next = (rows || []).map((item: Record<string, unknown>) => normalizeProject(item));
    setProjects(next);
    setProjectId((current) => current && next.some((item) => item.id === current) ? current : next[0]?.id || null);
  };

  useEffect(() => {
    if (!isOpen) return;
    void load().catch(() => setNotice('Не удалось открыть научные проекты.'));
  }, [isOpen]);

  if (!isOpen) return null;

  const project = projects.find((item) => item.id === projectId) || projects[0] || null;

  const runAction = async (body: Record<string, unknown>) => {
    if (!project) return;
    setNotice('');
    try {
      if (typeof supervisedStudentApi.projectAction === 'function') {
        await supervisedStudentApi.projectAction(project.id, body);
        await load();
      } else if (body.action === 'upload') {
        await supervisedStudentApi.submitProjectResult(project.id, { title: body.title, file_url: body.file_url });
      }
      setNotice('Сохранено в проекте.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Не удалось сохранить действие.');
    }
  };

  return (
    <div className="tp201-root" role="dialog" aria-modal="true">
      <header className="tp201-top">
        <button type="button" onClick={onClose}>← Назад</button>
        <div>
          <h1>Научные проекты</h1>
          <p>STUDENT · проекты с научным руководителем</p>
        </div>
        <button type="button" onClick={onClose}>Закрыть ×</button>
      </header>
      <main className="tp201-content">
        {!project ? (
          <section className="sv121-panel" style={{ ['--c' as string]: '#49ddb7' }}>
            <h3>Научных проектов пока нет</h3>
            <p>Карточка станет рабочей автоматически, когда научный руководитель добавит вас в научный проект.</p>
          </section>
        ) : (
          <>
            <div className="tp201-standalone-note">Здесь отображаются только научные проекты, в которые вас добавил научный руководитель. Данные проекта общие для обоих кабинетов.</div>
            <section className="tp201-project-switch">
              {projects.map((item) => (
                <button key={item.id} type="button" className={item.id === project.id ? 'active' : ''} onClick={() => { setProjectId(item.id); setTab('overview'); setSample(null); }}>
                  <small>{item.stage}</small>
                  <strong>{item.title}</strong>
                  <span>{item.role} · {item.progress}%</span>
                </button>
              ))}
            </section>
            <section className="tp201-project-workspace">
              <aside>
                <nav className="tp201-project-nav">
                  {TABS.map(([id, label], index) => (
                    <button key={id} type="button" className={tab === id ? 'active' : ''} onClick={() => { setTab(id); setSample(null); }}>
                      <span className="ico">{String(index + 1).padStart(2, '0')}</span>
                      <span><strong>{label}</strong><small>Научный проект</small></span>
                      <span>›</span>
                    </button>
                  ))}
                </nav>
              </aside>
              <main>
                <ProjectPane
                  project={project}
                  tab={tab}
                  consultText={consultText}
                  sample={sample}
                  onConsultText={setConsultText}
                  onSample={setSample}
                  onOpenLiterature={onOpenLiterature}
                  onAction={(body) => void runAction(body)}
                />
              </main>
            </section>
          </>
        )}
        {notice ? <p className="tp201-standalone-note">{notice}</p> : null}
      </main>
    </div>
  );
};

const StatusBadge: React.FC<{ value: string }> = ({ value }) => {
  const [label, kind] = statusOf(value);
  return <span className={`tp201-status ${kind}`}>{label}</span>;
};

const ProjectPane: React.FC<{
  project: ResearchProject;
  tab: ProjectTab;
  consultText: string;
  sample: string[] | null;
  onConsultText: (value: string) => void;
  onSample: (value: string[] | null) => void;
  onOpenLiterature?: () => void;
  onAction: (body: Record<string, unknown>) => void;
}> = ({ project, tab, consultText, sample, onConsultText, onSample, onOpenLiterature, onAction }) => {
  if (tab === 'topic') {
    return (
      <section className="sv121-grid">
        <article className="sv121-card sv121-span7" style={{ ['--c' as string]: '#49ddb7' }}>
          <span className="sv121-eyebrow">ТЕМА ПРОЕКТА</span>
          <h3>{project.topic || project.title}</h3>
          <p>{project.goal || project.description}</p>
          <div className="tp201-mini">
            <article style={{ ['--c' as string]: '#41d2ff' }}><small>Моя роль</small><b style={{ fontSize: 14 }}>{project.role}</b><p>индивидуальный вклад</p></article>
            <article style={{ ['--c' as string]: '#e8b33b' }}><small>Руководство</small><b style={{ fontSize: 14 }}>{project.supervisor || 'Научный руководитель'}</b><p>научный контроль</p></article>
          </div>
        </article>
        <article className="sv121-card sv121-span5" style={{ ['--c' as string]: '#9e80ff' }}>
          <span className="sv121-eyebrow">ГРАНИЦЫ ПРОЕКТА</span>
          <h3>Что фиксируется</h3>
          <div className="sv121-list">
            <div className="sv121-row"><div><strong>Тема и цель</strong><small>Утверждённая версия и история изменений</small></div></div>
            <div className="sv121-row"><div><strong>Роли участников</strong><small>Обязанности и ожидаемые результаты</small></div></div>
            <div className="sv121-row"><div><strong>Срок проекта</strong><small>{project.period || project.deadline}</small></div></div>
          </div>
        </article>
      </section>
    );
  }
  if (tab === 'plan') {
    return (
      <section className="sv121-card" style={{ ['--c' as string]: '#41d2ff' }}>
        <header><div><span className="sv121-eyebrow">ПЛАН И ЭТАПЫ</span><h3>{project.title}</h3><p>Каждый этап имеет срок, статус и ожидаемый результат.</p></div></header>
        <div className="tp201-project-timeline">
          {project.tasks.map((task, index) => (
            <article key={task.title} style={{ ['--c' as string]: task.status === 'DONE' || task.status === 'Выполнено' ? '#49ddb7' : task.status === 'IN_PROGRESS' || task.status === 'В работе' ? '#e8b33b' : '#41d2ff' }}>
              <b>{String(index + 1).padStart(2, '0')}</b>
              <div><strong>{task.title}</strong><small>Срок: {task.deadline}</small></div>
              <StatusBadge value={task.status} />
            </article>
          ))}
        </div>
      </section>
    );
  }
  if (tab === 'tasks') {
    return (
      <section className="sv121-grid">
        <article className="sv121-card sv121-span7" style={{ ['--c' as string]: '#e8b33b' }}>
          <header><div><span className="sv121-eyebrow">МОИ ЗАДАЧИ</span><h3>Что требуется от меня</h3></div></header>
          <div className="sv121-list">
            {project.tasks.map((task) => (
              <div key={task.title} className="sv121-row"><div><strong>{task.title}</strong><small>Дедлайн {task.deadline}</small></div><StatusBadge value={task.status} /></div>
            ))}
          </div>
          <div className="sv121-actions">
            <label className="sv121-btn gold">
              Загрузить результат / новую версию
              <input type="file" hidden onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onAction({ action: 'upload', title: file.name, file_url: file.name });
                event.target.value = '';
              }} />
            </label>
          </div>
        </article>
        <article className="sv121-card sv121-span5" style={{ ['--c' as string]: '#41d2ff' }}>
          <header><div><span className="sv121-eyebrow">МАТЕРИАЛЫ ПРОЕКТА</span><h3>Рабочие файлы</h3></div></header>
          <div className="sv121-list">
            {project.materials.map((name, index) => (
              <div key={name} className="sv121-row"><div><strong>{name}</strong><small>Версия {index + 1} · общий материал проекта</small></div><span className="tp201-status info">ДОСТУПНО</span></div>
            ))}
          </div>
        </article>
      </section>
    );
  }
  if (tab === 'literature') {
    return (
      <section className="sv121-card" style={{ ['--c' as string]: '#66b7e8' }}>
        <header><div><span className="sv121-eyebrow">ЛИТЕРАТУРА ПРОЕКТА</span><h3>Общие и сохранённые источники</h3><p>Проектная литература отделена от библиографии учебной дисциплины.</p></div></header>
        <div className="sv121-list">
          {project.literature.map((title, index) => (
            <div key={title} className="sv121-row">
              <div><strong>{title}</strong><small>Источник проекта {String(index + 1).padStart(2, '0')}</small></div>
              <button type="button" className="sv121-btn" onClick={() => onAction({ action: 'literature', literature_index: index, title })}>
                {project.savedLiterature.includes(title) ? 'Сохранено' : 'Сохранить'}
              </button>
            </div>
          ))}
        </div>
        <div className="sv121-actions">
          <button type="button" className="sv121-btn mint" onClick={onOpenLiterature}>Найти дополнительную литературу</button>
        </div>
      </section>
    );
  }
  if (tab === 'templates') {
    return (
      <>
        <section className="sv121-panel" style={{ ['--c' as string]: '#9e80ff' }}>
          <span className="sv121-eyebrow">ОБРАЗЦЫ И ДОКУМЕНТЫ ПРОЕКТА</span>
          <h2>Шаблоны для исследовательской работы</h2>
          <p>Образцы связаны именно с научным проектом и не смешиваются с документами дисциплины.</p>
        </section>
        {sample ? (
          <section className="sv121-card" style={{ ['--c' as string]: '#9e80ff', marginBottom: 12 }}>
            <span className="sv121-eyebrow">{sample[0]}</span>
            <h3>{sample[1]}</h3>
            <p>{sample[2]}</p>
          </section>
        ) : null}
        <div className="tp201-resource-grid">
          {TEMPLATES.map((item, index) => (
            <article key={item[0]} className="tp201-resource-card" style={{ ['--c' as string]: COLORS[index % COLORS.length] }}>
              <small>{item[0]}</small>
              <h3>{item[1]}</h3>
              <p>{item[2]}</p>
              <div className="sv121-actions"><button type="button" className="sv121-btn" onClick={() => onSample(item)}>Открыть образец</button></div>
            </article>
          ))}
        </div>
      </>
    );
  }
  if (tab === 'meetings') {
    return (
      <section className="sv121-grid">
        <article className="sv121-card sv121-span8" style={{ ['--c' as string]: '#49ddb7' }}>
          <header><div><span className="sv121-eyebrow">ВСТРЕЧИ И КОНСУЛЬТАЦИИ</span><h3>{project.title}</h3></div></header>
          <div className="tp201-calendar">
            {project.meetings.map((meeting) => (
              <div key={`${meeting.date}-${meeting.title}`} className="tp201-calendar-row">
                <time>{meeting.date}<br />{meeting.time}</time>
                <div><strong>{meeting.title}</strong><small>{project.supervisor}</small></div>
                <StatusBadge value={meeting.status} />
              </div>
            ))}
          </div>
        </article>
        <article className="sv121-card sv121-span4" style={{ ['--c' as string]: '#e8b33b' }}>
          <span className="sv121-eyebrow">ЗАПРОС</span>
          <h3>Нужна консультация?</h3>
          <p>Укажите вопрос и удобный формат. Запрос сохраняется внутри проекта.</p>
          <label className="tp201-field">
            <textarea value={consultText} placeholder="Вопрос к научному руководителю" onChange={(event) => onConsultText(event.target.value)} />
          </label>
          <div className="sv121-actions">
            <button type="button" className="sv121-btn gold" onClick={() => { onAction({ action: 'consult', title: consultText || 'Запрос консультации' }); onConsultText(''); }}>Запросить консультацию</button>
          </div>
        </article>
      </section>
    );
  }
  if (tab === 'results') {
    return (
      <section className="sv121-grid">
        <article className="sv121-card sv121-span7" style={{ ['--c' as string]: '#9e80ff' }}>
          <header><div><span className="sv121-eyebrow">ПРЕЗЕНТАЦИИ И РЕЗУЛЬТАТЫ</span><h3>Итоговые материалы проекта</h3></div></header>
          <div className="sv121-list">
            {project.results.map((item, index) => (
              <div key={item.title} className="sv121-row"><div><strong>{item.title}</strong><small>{index === 0 ? 'Индивидуальный результат' : 'Проектный результат'}</small></div><StatusBadge value={item.status} /></div>
            ))}
          </div>
          <div className="sv121-actions">
            <label className="sv121-btn gold">
              Загрузить результат
              <input type="file" hidden onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onAction({ action: 'upload', title: file.name });
                event.target.value = '';
              }} />
            </label>
            <button type="button" className="sv121-btn mint" onClick={() => onAction({ action: 'portfolio' })}>
              {project.inPortfolio ? '★ В академическом портфолио' : '☆ Добавить в академическое портфолио'}
            </button>
          </div>
        </article>
        <article className="sv121-card sv121-span5" style={{ ['--c' as string]: '#49ddb7' }}>
          <span className="sv121-eyebrow">ФИКСАЦИЯ ВКЛАДА</span>
          <h3>Мой вклад</h3>
          <p>Роль, выполненные задачи, файлы, консультации и итоговый результат формируют карточку участия в проекте.</p>
          <div className="tp201-note">Это отдельный научный контур и он не изменяет оценки по учебным дисциплинам.</div>
        </article>
      </section>
    );
  }
  const upcoming = project.tasks.filter((task) => task.status !== 'DONE' && task.status !== 'Выполнено' && task.status !== 'completed').slice(0, 3);
  return (
    <section className="sv121-grid">
      <article className="sv121-card sv121-span7" style={{ ['--c' as string]: '#49ddb7' }}>
        <span className="sv121-eyebrow">АКТИВНЫЙ НАУЧНЫЙ ПРОЕКТ</span>
        <h2 style={{ margin: '6px 0' }}>{project.title}</h2>
        <p>{project.description}</p>
        <div className="tp201-mini">
          <article style={{ ['--c' as string]: '#49ddb7' }}><small>Прогресс</small><b>{project.progress}%</b><p>по проектному плану</p></article>
          <article style={{ ['--c' as string]: '#e8b33b' }}><small>Ближайший срок</small><b style={{ fontSize: 14 }}>{project.deadline}</b><p>следующая контрольная точка</p></article>
          <article style={{ ['--c' as string]: '#41d2ff' }}><small>Моя роль</small><b style={{ fontSize: 13 }}>{project.role}</b><p>в команде</p></article>
          <article style={{ ['--c' as string]: '#9e80ff' }}><small>Этап</small><b style={{ fontSize: 13 }}>{project.stage}</b><p>{project.period}</p></article>
        </div>
        <div className="tp201-progress" style={{ ['--w' as string]: `${project.progress}%` }}><span /></div>
      </article>
      <article className="sv121-card sv121-span5" style={{ ['--c' as string]: '#41d2ff' }}>
        <span className="sv121-eyebrow">СЛЕДУЮЩИЕ ДЕЙСТВИЯ</span>
        <h3>Что делать сейчас</h3>
        <div className="sv121-list">
          {upcoming.map((task) => (
            <div key={task.title} className="sv121-row"><div><strong>{task.title}</strong><small>{task.deadline}</small></div><StatusBadge value={task.status} /></div>
          ))}
        </div>
      </article>
    </section>
  );
};
